import React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";
import SearchType, {
    BulkSearchProductRequest, GetBulkProductSearchDataResponse, GetProductPageDataResponse,
} from "src/model/ProductMatch";
import MetricsUtil, {ActionName} from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import {KatBox, KatButton, KatDropdown, KatIcon, KatInput, KatLabel, KatOption} from "@amzn/katal-react";
import {getCountryListConfig} from "src/config/Config";
import RequestStatus from "src/model/RequestStatus";
import {setBulkProductDataQueryStart} from "src/actions/BulkProductSearchAction";

interface StateProps {
    bulkProductSearchRes: GetBulkProductSearchDataResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    handleCountryChange: (countryCode: string) => void
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    bulkProductSearchRes: state.bulkProductSearchRes,
});

interface DispatchProps {
    readonly dispatchGetProduct: (request: BulkSearchProductRequest) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchGetProduct: setBulkProductDataQueryStart,
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    searchKey: string,
    minPrice: string,
    maxPrice: string,
    fulfilledBy: string
}

export class SearchMyProductBarComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        this.state = {
            searchKey: '',
            minPrice: '',
            maxPrice: '',
            fulfilledBy: 'All'
        };

        this.handleKeyup = this.handleKeyup.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.doSubmit = this.doSubmit.bind(this);
        this.handleKeyChange = this.handleKeyChange.bind(this);
        this.handleMinPriceChange = this.handleMinPriceChange.bind(this);
        this.handleMaxPriceChange = this.handleMaxPriceChange.bind(this);
        this.handleFulfilledByChange = this.handleFulfilledByChange.bind(this);
        this.handleChangeCountry = this.handleChangeCountry.bind(this);

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
    }

    handleKeyChange(event: KatInput.ChangeEvent) {
        this.setState({searchKey: event.currentTarget.value});
        this.publishCounter("searchKey", "input");
    }

    handleMinPriceChange(event: KatInput.ChangeEvent) {
        this.setState({minPrice: event.currentTarget.value});
        this.publishCounter("minPrice", "input");
    }

    handleMaxPriceChange(event: KatInput.ChangeEvent) {
        this.setState({maxPrice: event.currentTarget.value});
        this.publishCounter("maxPrice", "input");
    }

    handleFulfilledByChange(event: KatDropdown.ChangeEvent) {
        this.setState({fulfilledBy: event.detail.value});
        this.publishCounter("fulfilledBy", "select");
    }

    handleChangeCountry(event: KatDropdown.ChangeEvent) {
        this.props.handleCountryChange(event.detail.value);
        this.publishCounter("existingProduct_countryCode", "select");
    }

    publishCounter(method: string, counter: string) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(method, counter);
        }
    }

    handleKeyup(event: KatInput.KeyupEvent) {
        let key = (event as unknown as KeyboardEvent).key;
        if (key === "Enter" || key === "NumpadEnter") {
            event.preventDefault();
            this.doSubmit();
        }
    }

    handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        this.doSubmit();
    }

    doSubmit() {
        const countryCode = this.props.currentCountryCode;
        const searchKey = this.state.searchKey;

        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchButton", "click",);
            this.metricsUtil.setLongTimer(ActionName.TIME_ON_PAGE_AFTER_PRODUCT_SEARCHING_OR_SELECTING);
        }
        if (!countryCode || !searchKey.trim()) {
            return;
        }

        const filters: Record<string, string> = {};
        this.state.minPrice != "" ? filters["MIN_PRICE"] = this.state.minPrice : null;
        this.state.maxPrice != "" ? filters["MAX_PRICE"] = this.state.maxPrice : null;
        this.state.fulfilledBy == "Amazon" ? filters["FULFILLMENT"] = "Y" : null;
        this.state.fulfilledBy == "Merchant" ? filters["FULFILLMENT"] = "N" : null;
        let request: BulkSearchProductRequest = {
            pageOffset: 1,
            searchType: SearchType.MERCHANT,
            countryCode: countryCode,
            keywords: searchKey,
            filters: filters
        }
        this.props.dispatchGetProduct(request);
    }

    render() {
        const {t} = this.props;
        const countryCode = this.props.currentCountryCode;

        return (
            <>
                <KatBox variant="ltgrey">
                    <form id="ProductSearchInput" onSubmit={this.handleSubmit} className="product-search-input">
                        <KatLabel className="product-search-label-filter bulk-search-label"
                                  text={t('SC_FBA_Calc_bulk_filters')}></KatLabel>
                        <KatLabel text={t('SC_FBA_Calc_label_country') + ":"} className="bulk-search-label"></KatLabel>
                        <KatDropdown richSelectionLabel value={countryCode}
                                     className="bulk-search-dropdown" onChange={this.handleChangeCountry}>
                            {
                                getCountryListConfig().map((country, index) => (
                                    <KatOption value={country} key={index}>
                                        <div className="flexed">
                                            <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name} size="small"/>
                                            <span className="paddedMedium">{country}</span>
                                        </div>
                                    </KatOption>
                                ))
                            }
                        </KatDropdown>

                        <KatInput
                            placeholder={t('SC_FBA_Calc_tip_search')} className="bulk-search-input-asin" onChange={this.handleKeyChange}
                            onKeyup={this.handleKeyup}/>

                        <KatLabel text={t('SC_FBA_Calc_bulk_fulfilled_by')} className="bulk-search-label"></KatLabel>
                        <KatDropdown value="All" className="bulk-search-dropdown" onChange={this.handleFulfilledByChange}
                                     options={[
                                         {name: t("SC_FBA_Calc_bulk_all"), value: "All"},
                                         {name: t("SC_FBA_Calc_bulk_amazon"), value: "Amazon"},
                                         {name: t("SC_FBA_Calc_bulk_merchant"), value: "Merchant"},
                                     ]}/>

                        <KatLabel text={t('SC_FBA_Calc_bulk_price')} className="bulk-search-label"></KatLabel>
                        <KatInput placeholder={t("SC_FBA_Calc_bulk_min")} type="text" onChange={this.handleMinPriceChange}
                                  className="bulk-search-input-price"/> -
                        <KatInput placeholder={t("SC_FBA_Calc_bulk_max")} type="text" onChange={this.handleMaxPriceChange}
                                  className="bulk-search-input-price"/>

                        <KatButton label={t('SC_FBA_KnightsSearchButtonLabel_35008')} variant="primary" type="submit"
                                   disabled={this.props.bulkProductSearchRes.requestStatus === RequestStatus.LOADING}
                                   className="bulk-search-button-search"/>
                    </form>
                </KatBox>
            </>
        );
    }
}

export default withTranslation()(connector(SearchMyProductBarComponent));


